import React, { useMemo, useRef, useCallback } from "react";
import { observer } from "mobx-react";
import { logEvent } from '@amplitude/analytics-browser'; // ← browser SDK
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Paper } from "@mantine/core";

import Text from "components/Text/Text"
import styles from './BaseballCard.module.scss';
import { ReactComponent as MailIcon } from "assets/email.svg";
import { ReactComponent as SummaryIcon } from "assets/summary.svg";
import { ReactComponent as DetailsIcon } from "assets/details.svg";
import { ReactComponent as CheckInIcon } from "assets/check_in.svg";
import { ReactComponent as NotesIcon } from "assets/notes.svg";
import { ReactComponent as NavigationArrowIcon } from "assets/navigationArrowRight.svg";
import { ReactComponent as StatsIcon } from "assets/stats.svg";
import { ReactComponent as MoneyIcon } from "assets/CurrencyCircleDollar.svg";
import { ReactComponent as InfoIcon } from 'assets/info.svg';
import { ReactComponent as LeftIcon } from "assets/caretleft.svg";
import { NavigationService, NavigationApp } from "helpers/navigation";
import { copyToClipboard } from "helpers/clipboard";

import SendTab from "./components/SendTab";
import Summary from "./modules/Summary";
import Notes from "./modules/Notes";
import CheckIn from "./modules/CheckIn";
import { store } from "stores/Store";
import PractitionerDetails from "./modules/Details";
import Opportunities, { Opportunity } from "Modules/Opportunities";
import useExternalApiService from "helpers/ExternalApiService";
import Stats from "./modules/Stats";
import { getLeadStateByPractitioner } from "helpers/lead";
import RoutesTab from './components/RoutesTab';
import Toastr from "components/Toastr";
import FollowUpMenu from "./components/FollowUpMenu";
import EditFollowUpDateMenu from "./components/EditFollowUpDateMenu";
import { openLeadDrawer } from "components/LeadDrawer";
import CreateTagMenu from "./modules/Summary/CreateTagMenu";
import EditTagsMenu from "./modules/Summary/EditTagsMenu";
import { AddressFormData } from "./components/AddressForm";
import AddressFormMenu from "./modules/Summary/AddressFormMenu";
import OpportunitiesFormMenu from "./modules/OpportunitiesFormMenu";
import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";

export enum Tab {
  SUMMARY = "summary",
  DETAILS = "details",
  CHECK_IN = "check_in",
  NOTES = "notes",
  OPPORTUNITIES = "opportunities",
  STATS = "stats",
}

interface ITag {
  id: number;
  name: string;
  selected: boolean;
}

interface Props {
  defaultTab?: Tab;
  generalNotes: string;
  backHandler: () => void;
  onEditLead?: (lead: any) => void;
  noBackNav?: boolean;
  onFindOnMap?: (practitioner: any, latLon: [number, number]) => void;
  onAddressChange?: () => void;
  onLeadDeleted?: () => void;
}

const BaseballCard = observer(({ defaultTab, generalNotes, backHandler, noBackNav, onEditLead, onFindOnMap, onAddressChange, onLeadDeleted }: Props) => {
  const [selectedTab, setSelectedTab] = React.useState<Tab>(defaultTab || Tab.SUMMARY);
  const [isOpen, setIsOpen] = React.useState(false);
  const [showGrade, setShowGrade] = React.useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = React.useState<boolean>(false);
  const [distance, setDistance] = React.useState<number | null>(null);
  const [isCalculatingDistance, setIsCalculatingDistance] = React.useState<boolean>(false);
  const [isGoRouteModalOpen, setIsGoRouteModalOpen] = React.useState(false);
  const [showToastr, setShowToastr] = React.useState(false);
  const [showCopyToast, setShowCopyToast] = React.useState(false);
  const [isFollowUpMenuOpen, setIsFollowUpMenuOpen] = React.useState(false);
  const [isEditFollowUpDateMenuOpen, setIsEditFollowUpDateMenuOpen] = React.useState(false);
  const [isLoadingFollowUpDates, setIsLoadingFollowUpDates] = React.useState(false);
  const [isCreateTagMenuOpen, setIsCreateTagMenuOpen] = React.useState(false);
  const [isEditTagsMenuOpen, setIsEditTagsMenuOpen] = React.useState(false);

  const [isAddressFormOpen, setIsAddressFormOpen] = React.useState(false);
  const [addressEditPractitionerId, setAddressEditPractitionerId] = React.useState<string>('');
  const [addressEditId, setAddressEditId] = React.useState<string | undefined>();
  const [addressFormInitialData, setAddressFormInitialData] = React.useState<Partial<AddressFormData> | undefined>();

  const [selectedOppOrRisk, setSelectedOppOrRisk] = React.useState<any>(null);
  const [opportunities, setOpportunities] = React.useState<Opportunity[]>([]);

  // Delete lead state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [isDeletingLead, setIsDeletingLead] = React.useState(false);

  // AI Email Draft state
  const [isEmailDraftLoading, setIsEmailDraftLoading] = React.useState(false);
  const [emailDraftError, setEmailDraftError] = React.useState<string | undefined>();
  const [emailDraftResult, setEmailDraftResult] = React.useState<{ subject: string; body: string } | undefined>();
  const emailDraftAbortControllerRef = useRef<AbortController | null>(null);

  const isModalMenuOpen = useMemo(() =>
    isFollowUpMenuOpen || isEditFollowUpDateMenuOpen || isCreateTagMenuOpen || isEditTagsMenuOpen || isAddressFormOpen || selectedOppOrRisk,
    [isFollowUpMenuOpen, isEditFollowUpDateMenuOpen, isCreateTagMenuOpen, isEditTagsMenuOpen, isAddressFormOpen, selectedOppOrRisk],
  );

  const fetchFollowUpsAbortControllerRef = useRef<AbortController | null>(null);

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { getData, postData, patchData } = useExternalApiService(apiUrl);

  const defaultAddress = store.currentPractitioner?.addresses
    ?.find((a: any) => a.id === store.currentPractitioner?.default_address);
  // Pull lat & lon out once so our JSX can read them
  const lat = defaultAddress?.latitude;
  const lon = defaultAddress?.longitude;

  const tags = store.currentPractitioner?.tags ?? { mine: [], others: [] };
  const netsuiteId = store.currentPractitioner?.netsuite_id;

  React.useEffect(() => {
    if (store.checkIn?.practitioner_name) {
      setCurrentPractitioner();
    }
    if (!defaultTab) {
      const latestVisitedTab = store.latestVisitedTab.get(
        store.currentPractitioner.id
      );
      if (latestVisitedTab) {
        setSelectedTab(latestVisitedTab);
      }
    }
    
    fetchFollowUpDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (defaultAddress) {
      setIsCalculatingDistance(true);
      /*
      calculteDistance()?.then((distance: number | null) => {
        if (distance !== null) {
          setDistance(distance);
        }
        setIsCalculatingDistance(false);
      }).catch(() => {
        setIsCalculatingDistance(false);
      });
      */
      const promise = calculteDistance();
      if (!promise) {
        // calculteDistance() returned null for some reason—bail out:
        setIsCalculatingDistance(false);
        return;
      }

      promise
        .then((d) => {
          if (d !== null) {
            setDistance(d);
          }
        })
        .catch(() => {
          // geolocation was denied or failed
        })
        .finally(() => {
          setIsCalculatingDistance(false);
        });
        //trackClick("View practitioner");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAddress]);

  // Helper to track all button clicks with a consistent event
  const trackClick = (section: string) => {
    const label = 'Baseballcard: ' + section
    logEvent(label, { section });
  };

  const calculteDistance = () => {
    if (!lat || !lon) {
      return null;
    }

    // Get current position from browser
    if ("geolocation" in navigator) {
      return new Promise<number>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition((position) => {
          const lat1 = position.coords.latitude;
          const lon1 = position.coords.longitude;
          const lat2 = lat;
          const lon2 = lon;

          // Calculate distance using Haversine formula
          const R = 6371; // Radius of earth in km
          const dLat = (lat2 - lat1) * Math.PI / 180;
          const dLon = (lon2 - lon1) * Math.PI / 180;
          const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          const distance = R * c;

          resolve(Math.round(distance * 0.621371)); // Convert to miles and round
        }, (error) => {
          console.log("erro" ,error);
          reject(error);
        });
      });
    }

    return null;
  }
  const setCurrentPractitioner = async () => {
    const result = await postData(`/api/get_practitioner_by_primary_name`, {
      "practitioner_name": store.checkIn?.practitioner_name
    });
    store.setCurrentPractitioner({ ...result.practitioner, general_notes: generalNotes });
  }

  const fetchFollowUpDates = React.useCallback(async () => {
    if (!store.currentPractitioner?.id) return;

    setIsLoadingFollowUpDates(true);

    const abortController = new AbortController();
    fetchFollowUpsAbortControllerRef.current = abortController;

    store.setCurrentPractitionerLastCheckinDate(null);

    try {
      const response = await getData(`/api/practitioner/${store.currentPractitioner?.id}/followups?netsuite_id=${store.currentPractitioner?.netsuite_id ?? ''}&bg_id=${store.currentPractitioner?.bg_rowid ?? ''}`, abortController.signal);
      store.setCurrentPractitioner({
        ...store.currentPractitioner,
      });
      store.setCurrentPractitionerLastCheckinDate(response.last_checkin_date);
      store.setCurrentPractitionerNextFollowUpDate(response.next_followup_date);

      setIsLoadingFollowUpDates(false);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }

      setIsLoadingFollowUpDates(false);
    }
  }, [getData]);

  const handleSetFollowUpDate = async (date: string) => {
    await postData(`/api/practitioner/${store.currentPractitioner?.id}/followup`, { followup_date: date });
    store.toggleFollowUpPractitionersUpdateFlag();
    setIsFollowUpMenuOpen(false);
    setIsEditFollowUpDateMenuOpen(false);

    fetchFollowUpDates();
  };

  const handleCompleteFollowUp = async () => {
    await postData(`/api/practitioner/${store.currentPractitioner?.id}/followup/complete`);
    store.toggleFollowUpPractitionersUpdateFlag();

    fetchFollowUpDates();
  };

  const handleClearFollowUpDate = async () => {
    await postData(`/api/practitioner/${store.currentPractitioner?.id}/followup/clear`);
    store.toggleFollowUpPractitionersUpdateFlag();
    setIsFollowUpMenuOpen(false);
    setIsEditFollowUpDateMenuOpen(false);

    fetchFollowUpDates();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updatePractitionerCache = React.useCallback((updatedTags: ITag[]) => {
    if (!store.impersonatedUser) return;
    const currentUser = store.impersonatedUser;
    const userCache = store.practitionersCache[currentUser];
    if (userCache) {
      Object.keys(userCache).forEach(pageKey => {
        const page = parseInt(pageKey);
        const cachedPractitioners = store.getPractitionersCache(currentUser, page);
        if (cachedPractitioners) {
          const updatedCache = cachedPractitioners.map((practitioner: any) =>
            practitioner.id === store.currentPractitioner?.id
              ? {...practitioner, tags: updatedTags}
              : practitioner
          );
          store.setPractitionersCache(currentUser, page, updatedCache);
        }
      });
    }
  }, []);

  const refetchTags = React.useCallback(async () => {
    const response = await getData(`/api/practitioner/${store.currentPractitioner?.id}/tags`);
    store.setCurrentPractitioner({...store.currentPractitioner, tags: response.tags});
  }, [getData]);

  const handleTagCreation = async (tagName: string) => {
    await postData('/api/practitioner/tags/create', {tag: tagName});

    refetchTags();
  };

  const handleTagEdits = async (updatedTags: {id: number, name: string}[], deletedTags: number[]) => {
    if (updatedTags.length > 0) {
      await patchData('/api/practitioner/tags/update', {tags: updatedTags});
    }

    if (deletedTags.length > 0) {
      await postData('/api/practitioner/tags/delete', {tag_ids: deletedTags});

      if (store.impersonatedUser) {
        const currentUser = store.impersonatedUser;
        const userCache = store.practitionersCache[currentUser];
        if (userCache) {
          Object.keys(userCache).forEach(pageKey => {
            const page = parseInt(pageKey);
            const cachedPractitioners = store.getPractitionersCache(currentUser, page);
            if (cachedPractitioners) {
              const updatedCache = cachedPractitioners.map((practitioner: any) => {
                if (practitioner.tags?.mine && practitioner.tags.mine.length > 0) {
                  const filteredTags = practitioner.tags.mine.filter((tag: ITag) => !deletedTags.includes(tag.id));
                  return {...practitioner, tags: {...practitioner.tags, mine: filteredTags}};
                }
                return practitioner;
              });
              store.setPractitionersCache(currentUser, page, updatedCache);
            }
          });
        }
      }
    }

    refetchTags();
  };

  const fetchOppsAndRisks = (practId: string) => {
    getData(`/api/${practId}/get_opportunities`).then(
      (r) => {
        setOpportunities(r.opportunities ?? []);
      }
    );
  }

  const handleDeleteLead = async () => {
    setIsDeletingLead(true);
    try {
      await postData(`/api/delete_practitioner_from_chat`, {
        practitioner_id: store.currentPractitioner?.id
      });

      // Track deletion event
      logEvent('Baseballcard: Delete Lead', {
        practitioner_id: store.currentPractitioner?.id,
        practitioner_name: store.currentPractitioner?.name
      });

      // Clear leads cache to refresh the list
      store.clearLeadsCache();

      // Clear current practitioner
      store.setCurrentPractitioner(null);

      // Navigate back to leads tab if callback provided, otherwise just go back
      if (onLeadDeleted) {
        onLeadDeleted();
      } else if (backHandler) {
        backHandler();
      }
    } catch (error) {
      console.error('Failed to delete lead:', error);
    } finally {
      setIsDeletingLead(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleContact = useCallback(async (
    opt: 'email-self' | 'email-draft' | 'phone' | 'website',
    bulletPoints?: string,
    useRecentCheckingNotes?: boolean,
    useAccountNotes?: boolean,
  ) => {
    switch (opt) {
      case 'email-self':
        window.open(`mailto:${store.currentPractitioner?.email}`, '_blank');
        setIsOpen(false);
        break;
      case 'email-draft':
        // Call the AI email draft API
        setIsEmailDraftLoading(true);
        setEmailDraftError(undefined);
        setEmailDraftResult(undefined);

        const abortController = new AbortController();
        emailDraftAbortControllerRef.current = abortController;

        try {
          const response = await postData('/api/e/winback_draft_email_to_practitioner', {
            practitioner_id: store.currentPractitioner?.id,
            user_email: store.impersonatedUser,
            bullet_points: bulletPoints || '',
            use_recent_checkin_notes: useRecentCheckingNotes || false,
            use_account_notes: useAccountNotes || false,
          }, abortController.signal);

          if (response.subject && response.body) {
            setEmailDraftResult({ subject: response.subject, body: response.body });
            logEvent('AI Email Draft: Success', {
              practitioner_id: store.currentPractitioner?.id,
            });
          } else {
            setEmailDraftError('Failed to generate email draft. Please try again.');
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            return;
          }
          console.error('Email draft error:', error);
          setEmailDraftError('Failed to generate email draft. Please try again.');
          logEvent('AI Email Draft: Error', {
            practitioner_id: store.currentPractitioner?.id,
            error: error.message,
          });
        } finally {
          setIsEmailDraftLoading(false);
        }
        break;
      case 'phone':
        window.open(`tel:${store.currentPractitioner?.phone}`, '_blank');
        setIsOpen(false);
        break;
      case 'website':
        window.open(`${store.currentPractitioner?.website}`, '_blank');
        setIsOpen(false);
        break;
    }
  }, [postData]);

  const cancelEmailDraftRequest = useCallback(() => {
    emailDraftAbortControllerRef.current?.abort();
    emailDraftAbortControllerRef.current = null;
    setIsEmailDraftLoading(false);
    setEmailDraftError(undefined);
  }, []);

  const openEmailDraftLink = useCallback(() => {
    if (emailDraftResult) {
      const mailto = `mailto:${store.currentPractitioner?.email}?subject=${encodeURIComponent(emailDraftResult.subject)}&body=${encodeURIComponent(emailDraftResult.body)}`;
      window.open(mailto, '_blank');
      setIsOpen(false);
      // Reset draft state after opening
      setEmailDraftResult(undefined);
    }
  }, [emailDraftResult]);

  React.useEffect(() => {
    setIsHeaderHidden(false);

    if (selectedTab) {
      trackClick(`${selectedTab} tab`);
      console.log("tracking ", `${selectedTab}`);
    }
  }, [selectedTab])

  React.useEffect(() => {
    if (store.currentPractitioner?.id) {
      fetchFollowUpDates();
    }

    return () => {
      fetchFollowUpsAbortControllerRef.current?.abort();
      fetchFollowUpsAbortControllerRef.current = null;
      setIsLoadingFollowUpDates(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.currentPractitioner?.id]);

  React.useEffect(() => {
    // If there is no netsuiteId, don’t attempt to fetch opportunities at all.
    if (!netsuiteId) {
      return;
    }

    // Otherwise get the opportunities for this practitioner
    const practId = store?.currentPractitioner?.id;
    fetchOppsAndRisks(practId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [netsuiteId]);

  const renderTabContent = () => {
    switch (selectedTab) {
      case Tab.SUMMARY:
        return (
          <Summary
            isLoadingFollowUpDates={isLoadingFollowUpDates}
            isCreateTagMenuOpen={isCreateTagMenuOpen}
            isEditTagsMenuOpen={isEditTagsMenuOpen}
            navigate={setSelectedTab}
            onOpenFollowUpMenu={() => setIsFollowUpMenuOpen(true)}
            onClickEditFollowUpDate={() => setIsEditFollowUpDateMenuOpen(true)}
            onCompleteFollowUp={handleCompleteFollowUp}
            onOpenCreateTagMenu={() => setIsCreateTagMenuOpen(true)}
            onOpenEditTagsMenu={() => setIsEditTagsMenuOpen(true)}
          />
        );
      case Tab.NOTES:
        return <Notes />
      case Tab.CHECK_IN:
        return <CheckIn setIsHeaderHidden={setIsHeaderHidden} onCheckinAdded={fetchFollowUpDates} />
      case Tab.DETAILS:
        return (
          <PractitionerDetails
            navigate={setSelectedTab}
            onAddressChange={onAddressChange}
            onOpenAddressForm={(practitionerId: string, addressId?: string, initialData?: Partial<AddressFormData>) => {
              setAddressEditPractitionerId(practitionerId);
              setAddressEditId(addressId);
              setAddressFormInitialData(initialData);
              setIsAddressFormOpen(true);
            }}
          />
        );
      case Tab.STATS:        
        return <Stats />
      default:        
        return <Opportunities opportunities={opportunities} onSelectOppOrRisk={setSelectedOppOrRisk} />
    }
  }

  const handleRoutes = (opt: string) => {
    if (!defaultAddress) return;

    // EIS-979 Part 1: coords-FIRST via the shared NavigationService — the
    // default address' lat/lng wins; the address search is only the fallback
    // when no usable coordinates exist (malformed addresses used to produce a
    // blank Google result, e.g. jmontague -> "Adam Breiner").
    NavigationService.navigateToLocation(defaultAddress, opt as NavigationApp, {
      onError: (error) => console.error(`${opt} navigation failed:`, error),
    });
    setIsGoRouteModalOpen(false);
  };

  const handlePractitionerAdded = () => {
    setShowToastr(true);
  };

  const isEditable = ((store.currentPractitioner?.status === "LEAD-READY2SUBMIT")  || (store.currentPractitioner.status === "LEAD-INCOMPLETE"))

  return (
    <div
      style={{
        height: isModalMenuOpen ? '100%' : 'auto',
        overflow: isModalMenuOpen ? 'hidden' : 'auto',
      }}
    >
      {(!isHeaderHidden) && (
        <div className={getLeadStateByPractitioner(store.currentPractitioner)  ? styles["tinted-border"] : ""}>
          <div>{getLeadStateByPractitioner(store.currentPractitioner)}</div>
        </div>
      )}
      <div
        className={styles.wrapper}
        style={{
          height: isModalMenuOpen ? '100%' : 'auto',
          overflow: isModalMenuOpen ? 'hidden' : 'auto',
        }}
      >
        {(!isHeaderHidden) &&
          <>
            {/*<ExpandIcon style={{ transform: 'rotate(180deg)' }} onClick={
              backHandler ? () =>backHandler() : () => setSelectedTab(Tab.PRACTITIONERS_LIST)
            } 
            width={22} height={22}             
            />*/}
            {!noBackNav && (
              <LeftIcon 
                onClick={backHandler}
                className={styles.leftIcon} />
            )}
            <div className={styles.headerWrapper}>
              <div>
                {/*<Text type="header">{store.currentPractitioner?.name}</Text>*/}
                {/*<ArrowLeft fill='black' onClick={() => setSelectedTab(Tab.PRACTITIONERS_LIST)}></ArrowLeft>*/}
                <div
                  style={{ cursor: 'pointer' }}
                  onTouchStart={() => {
                    const timer = setTimeout(() => {
                      copyToClipboard(store.currentPractitioner?.primary_name || '');
                      setShowCopyToast(true);
                    }, 500);
                    (window as any).__nameCopyTimer = timer;
                  }}
                  onTouchEnd={() => clearTimeout((window as any).__nameCopyTimer)}
                  onTouchCancel={() => clearTimeout((window as any).__nameCopyTimer)}
                  onMouseDown={() => {
                    const timer = setTimeout(() => {
                      copyToClipboard(store.currentPractitioner?.primary_name || '');
                      setShowCopyToast(true);
                    }, 500);
                    (window as any).__nameCopyTimer = timer;
                  }}
                  onMouseUp={() => clearTimeout((window as any).__nameCopyTimer)}
                  onMouseLeave={() => clearTimeout((window as any).__nameCopyTimer)}
                >
                  <Text type="header">{store.currentPractitioner?.primary_name}</Text>
                </div>
                {store.currentPractitioner?.specialty && (
                  <Text type="subHeader">{store.currentPractitioner.specialty}</Text>
                )}
                {(defaultAddress?.city || defaultAddress?.state) && (
                  <Text width={'140px'} type="subHeader">
                    {defaultAddress?.city && defaultAddress?.state
                      ? `${defaultAddress.city.replace(/,\s*$/, '')}, ${defaultAddress.state}`
                      : (defaultAddress?.city?.replace(/,\s*$/, '') || defaultAddress?.state)}
                  </Text>
                )}
                <div className={styles.locationWrapper} onClick={() => setIsGoRouteModalOpen(true)}>
                  {/*<Text type="subHeader" width={'100px'}>{`${isCalculatingDistance ? 'Calculating...' : distance ? `${distance} miles away` : 'Distance unavailable'}`}</Text>*/}
                  {isCalculatingDistance
                    ? (<Text type="subHeader" width={"100px"}>Calculating…</Text>)
                    : lat == null || lon == null
                      ? (<div className={styles.missingDistanceWrapper}>
                            <Text type="subHeader" width={"110px"}>Missing distance</Text>
                             <div className={styles.tooltipContainer}>
                              <InfoIcon width={14} height={14} style={{ 
                                marginLeft:0,  
                                padding:0,
                                display:'block'    
                                }} 
                              />
                              <span className={styles.tooltipText}>Latitude & Longitude Missing</span>
                            </div>
                         </div>
                        )
                      : distance == null
                        ? (<div className={styles.missingDistanceWrapper}>
                            <Text type="subHeader" width={"110px"}>Missing distance</Text>
                             <div className={styles.tooltipContainer}>
                              <InfoIcon width={14} height={14} style={{ 
                                marginLeft:0,  
                                padding:0,
                                display:'block'    
                                }} 
                              />
                              <span className={styles.tooltipText}>Did you enable app to use GPS?</span>
                            </div>
                         </div>
                        )
                        : (
                          <div className={styles.distanceWithIcon} onClick={() => setIsGoRouteModalOpen(true)}>
                            <Text type="subHeader" width={"140px"}>{`${distance} miles away`}</Text>
                            <NavigationArrowIcon width={20} height={20} />
                          </div>
                          )
                  }
                  
                  
                </div>

                {isEditable ? (
                  <button
                    onClick={() => setIsDeleteModalOpen(true)}
                    className={styles["delete-lead-button"]}
                    style={{ marginTop: '10px', marginRight: '4px' }}
                    disabled={isDeletingLead}>
                    DELETE LEAD
                  </button>
                ) : ""}
                {isEditable ? (<button
                  onClick={() => {
                    // Extract address fields without the id to prevent overwriting practitioner ID
                    const addressData = store.currentPractitioner.addresses[0];
                    const { id: addressId, ...addressWithoutId } = addressData || {};
                    const leadData = {
                      ...store.currentPractitioner,
                      ...addressWithoutId  // Spread address fields WITHOUT the id
                    };

                    if (onEditLead) {
                      // Use custom handler if provided
                      onEditLead(leadData);
                    } else {
                      // Default: open the lead drawer in edit mode
                      openLeadDrawer(leadData, {
                        editMode: true,
                      }, () => {
                        store.setCurrentPractitioner(null);
                      });
                    }
                  }}
                  className={styles["edit-lead-button"]}>
                  EDIT LEAD
                </button>) : ""}
                {/*
                <Text type="subHeader">{`Last contact: ${date2usDotDate(store.currentPractitioner?.last_contact)}`}</Text>
                */}
              </div>

              <div className={styles.columnWrapper}>
                <div onClick={() => setShowGrade(!showGrade)}
                  className={`${styles.gradeWrapper} ${store.currentPractitioner?.grade === 'A' ? styles.gradeA :
                    store.currentPractitioner?.grade === 'B' ? styles.gradeB :
                      store.currentPractitioner?.grade === 'C' ? styles.gradeC :
                        store.currentPractitioner?.grade === 'D' ? styles.gradeD : styles.gradeNone
                    }`}
                >
                  {showGrade ?
                    <div className={styles.gradeWrapper}>
                      <p className={styles.gradeText}>{store.currentPractitioner?.grade}</p>
                      <p className={styles.gradeText__small}>Grade</p>
                    </div> :
                    <img height='130px' src={store.currentPractitioner?.picture} alt="" />
                  }
                  
                </div>
                <MailIcon onClick={() => setIsOpen(true)} style={{ cursor: 'pointer', paddingTop: '10px' }} />
              </div>
            </div>
            <div className={styles.horizontalScrollWrapper}>
              <div
                onClick={() => {
                  setSelectedTab(Tab.SUMMARY);
                  store.setLatestVisitedTab(store.currentPractitioner.id, Tab.SUMMARY);
                }}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.SUMMARY ? styles.selectedTab : styles.disabledTab}`}
              >
                <SummaryIcon />
                <p>Summary</p>
              </div>
              <div
                onClick={() => {
                  setSelectedTab(Tab.DETAILS);
                  store.setLatestVisitedTab(store.currentPractitioner.id, Tab.DETAILS);
                }}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.DETAILS ? styles.selectedTab : styles.disabledTab}`}
              >
                <DetailsIcon />
                <p>Details</p>
              </div>
              <div
                onClick={() => {
                  setSelectedTab(Tab.CHECK_IN);
                  store.setLatestVisitedTab(store.currentPractitioner.id, Tab.CHECK_IN);
                }}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.CHECK_IN ? styles.selectedTab : styles.disabledTab}`}
              >
                <CheckInIcon />
                <p>Check-In</p>
              </div>
              <div
                onClick={() => {
                  setSelectedTab(Tab.NOTES);
                  store.setLatestVisitedTab(store.currentPractitioner.id, Tab.NOTES);
                }}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.NOTES ? styles.selectedTab : styles.disabledTab}`}
              >
                <NotesIcon />
                <p>Notes</p>
              </div>
              <div
                onClick={() => {
                  setSelectedTab(Tab.OPPORTUNITIES);
                  store.setLatestVisitedTab(store.currentPractitioner.id, Tab.OPPORTUNITIES);
                }}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.OPPORTUNITIES ? styles.selectedTab : styles.disabledTab}`}
              >
                <MoneyIcon />
                <p>Opp/Risk</p>
              </div>
              <div
                onClick={() => {
                  setSelectedTab(Tab.STATS);
                  store.setLatestVisitedTab(store.currentPractitioner.id, Tab.STATS);
                }}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.STATS ? styles.selectedTab : styles.disabledTab}`}
              >
                <StatsIcon />
                <p>Stats</p>
              </div>
            </div>

          </>
        }
        <div className={styles.tabContentWrapper}>
          {renderTabContent()}
        </div>

        <Toastr
          message="Practitioner added to route"
          isVisible={showToastr}
          onClose={() => setShowToastr(false)}
          backgroundColor="white"
          closeTime={2000}
        />
        <Toastr
          message="Copied!"
          isVisible={showCopyToast}
          onClose={() => setShowCopyToast(false)}
          backgroundColor="white"
          closeTime={1500}
        />
      </div>

      <RoutesTab isOpen={isGoRouteModalOpen} setIsOpen={setIsGoRouteModalOpen} onSelect={handleRoutes} 
        onFindOnMap={onFindOnMap}
        onPractitionerAdded={handlePractitionerAdded} />

      <SendTab
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onSelect={handleContact}
        isLoading={isEmailDraftLoading}
        error={emailDraftError}
        draftResult={emailDraftResult}
        cancelDraftRequest={cancelEmailDraftRequest}
        openDraftLink={openEmailDraftLink}
      />

      <FollowUpMenu
        isOpen={isFollowUpMenuOpen}
        onClose={() => setIsFollowUpMenuOpen(false)}
        onSubmit={handleSetFollowUpDate}
        onClear={handleClearFollowUpDate}
      />

      <EditFollowUpDateMenu
        isOpen={isEditFollowUpDateMenuOpen}
        onClose={() => setIsEditFollowUpDateMenuOpen(false)}
        onSubmit={handleSetFollowUpDate}
        onClear={handleClearFollowUpDate}
      />

      <CreateTagMenu
        isOpen={isCreateTagMenuOpen}
        onClose={() => setIsCreateTagMenuOpen(false)}
        onSubmit={handleTagCreation}
      />

      <EditTagsMenu
        isOpen={isEditTagsMenuOpen}
        tags={tags.mine}
        onClose={() => setIsEditTagsMenuOpen(false)}
        onSubmit={handleTagEdits}
      />

      <AddressFormMenu
        isOpen={isAddressFormOpen}
        practitionerId={addressEditPractitionerId}
        addressId={addressEditId}
        initialData={addressFormInitialData}
        onSave={() => {
          setIsAddressFormOpen(false);
          if (onAddressChange) {
            onAddressChange();
          }
        }}
        onClose={() => setIsAddressFormOpen(false)}
      />

      <OpportunitiesFormMenu
        isOpen={selectedOppOrRisk !== null}
        oppOrRisk={selectedOppOrRisk}
        onSave={() => {
          const practId = store?.currentPractitioner?.id;
          fetchOppsAndRisks(practId);
          setSelectedOppOrRisk(null);
        }}
        onClose={() => setSelectedOppOrRisk(null)}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteLead}
        title="Delete Lead"
        message={`Are you sure you want to delete ${store.currentPractitioner?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
});


export default BaseballCard;